/**
 * Unified navigation config — the SINGLE source of truth for the sidebar,
 * sub-nav, breadcrumb, and sitemap (ADR 0002 F5 "must-fix-first": the vanilla
 * kept SIDEBAR_SECTIONS and NAV_GROUPS as two drifting lists).
 *
 * - SIDEBAR_SECTIONS = what the sidebar renders (flat, role-gated by `g`).
 * - NAV_GROUPS       = hub→tab model that drives SubNav + breadcrumb + sitemap.
 * - NAV_BY_ROLE      = which groups each role may see. STAFF has NO compensation
 *                      group → enforces Staff-no-salary at the nav layer
 *                      (RequireRole double-gates the salary routes; see F6).
 *
 * Dynamic groups (evaluation / work_hub / data_capture) resolve their tabs from
 * an EvalContext passed in by the caller — no module-global state (the vanilla
 * read a mutable `_evalCtx`). In React this context comes from queries.
 */
import type { Role } from '@humanai/shared';

export interface NavTab {
  id: string;
  label: string;
  adv?: boolean;
  methodCode?: string;
}

export interface NavGroup {
  label: string;
  icon: string;
  tabs: NavTab[];
  dynamic?: boolean;
  staticHead?: NavTab[];
}

export interface NavItem {
  id: string;
  label: string;
  icon: string;
  /** group key (gates visibility via NAV_BY_ROLE) */
  g: string;
  /** advanced — hidden in "simple" UI mode */
  adv?: boolean;
}

export interface NavSection {
  id: string;
  title: string;
  items: NavItem[];
}

// ── Dynamic context (tenant-enabled methods + workhub modules) ─────────────────
export interface MethodCatalogEntry {
  code: string;
  name_th?: string | null;
  name?: string | null;
  existing_page?: string | null;
}

export interface WorkhubModule {
  id: string;
  group: string;
  tab_id: string;
}

export interface EvalContext {
  enabledMethods: string[];
  methodCatalog: MethodCatalogEntry[];
  workhubModules: WorkhubModule[];
  workhubEffective: string[];
}

export const EMPTY_EVAL_CONTEXT: EvalContext = {
  enabledMethods: [],
  methodCatalog: [],
  workhubModules: [],
  workhubEffective: [],
};

// ── localStorage keys (kept identical to vanilla so prefs survive cutover) ─────
export const UI_MODE_KEY = 'ep_ui_mode';
export const NAV_COLLAPSE_KEY = 'ep_nav_collapsed';
export const NAV_RAIL_KEY = 'ep_nav_rail';
export type UiMode = 'simple' | 'full';

// ── Evaluation base tabs (always shown) ────────────────────────────────────────
export const EVALUATION_BASE_TABS: NavTab[] = [
  { id: 'evaluation-guide', label: 'คู่มือ' },
  { id: 'evaluation', label: 'เครื่องมือประเมิน (catalog)' },
  { id: 'assessment-packages', label: 'Package การประเมิน' },
];

// ── Top-level navigation groups (hub → tabs) ───────────────────────────────────
// `satisfies` (not `: Record`) so known-key access stays non-undefined under
// noUncheckedIndexedAccess, while still validating the shape.
export const NAV_GROUPS = {
  dashboard: { label: 'หน้าหลัก', icon: 'layout-dashboard', tabs: [{ id: 'dashboard', label: 'หน้าหลัก' }] },
  calendar: { label: 'ปฏิทินของฉัน', icon: 'calendar', tabs: [{ id: 'calendar', label: 'ปฏิทินของฉัน' }] },
  system_overview: { label: 'ภาพรวมระบบ', icon: 'layout-panel-top', tabs: [{ id: 'system-overview', label: 'ภาพรวมระบบ' }] },
  workflow_guide: { label: 'ขั้นตอนการทำงาน', icon: 'workflow', tabs: [{ id: 'workflow-guide', label: 'ขั้นตอนการทำงาน' }] },
  org: {
    label: 'โครงสร้างองค์กร',
    icon: 'network',
    // SoT (ADR 0003 addendum): org structure is owned by PayPilot — Eval shows
    // it read-only. setup-wizard + templates (org creators) removed.
    tabs: [
      { id: 'org-guide', label: 'คู่มือ' },
      { id: 'org', label: 'ผังองค์กร' },
      { id: 'position-jd', label: 'หน้าที่แต่ละตำแหน่ง' },
      { id: 'competency', label: 'ทักษะที่ต้องมี' },
      { id: 'core-values', label: 'ค่านิยมองค์กร', adv: true },
    ],
  },
  people: {
    label: 'พนักงาน',
    icon: 'users',
    tabs: [
      { id: 'people-guide', label: 'คู่มือ' },
      { id: 'employees', label: 'รายชื่อพนักงาน' },
      { id: 'onboarding-journey', label: 'เส้นทางต้อนรับ (90 วัน)' },
      { id: 'probation', label: 'ทดลองงาน' },
      { id: 'career', label: 'ดาวเด่น & เส้นทางโต' },
    ],
  },
  data_capture: {
    label: 'เก็บข้อมูลผลงาน',
    icon: 'clipboard-edit',
    dynamic: true,
    staticHead: [
      { id: 'data-capture-guide', label: 'คู่มือ' },
      { id: 'capture-catalog', label: 'เลือกสิ่งที่จะบันทึก', adv: true },
    ],
    tabs: [
      { id: 'observations', label: 'บันทึกพฤติกรรม' },
      { id: 'incidents', label: 'เหตุการณ์เด่น' },
      { id: 'checkins', label: 'คุย 1:1 หัวหน้า-ลูกน้อง' },
      { id: 'quality-logs', label: 'งานผิด / แก้ซ้ำ', adv: true },
      { id: 'psychometric', label: 'ผลทดสอบบุคลิกภาพ', adv: true },
    ],
  },
  work_hub: {
    label: 'งาน & เป้าหมาย',
    icon: 'kanban-square',
    dynamic: true,
    staticHead: [
      { id: 'work-hub-guide', label: 'คู่มือ' },
      { id: 'tasks', label: 'งานที่ต้องทำ' },
      { id: 'task-templates', label: 'แม่แบบงาน', adv: true },
      { id: 'workhub-catalog', label: 'เลือกเครื่องมือ', adv: true },
    ],
    tabs: [
      { id: 'objectives', label: 'เป้าหมาย (OKR)' },
      { id: 'kpi-metrics', label: 'ตัวชี้วัด (KPI)' },
      { id: 'ranking', label: 'จัดอันดับพนักงาน', adv: true },
      { id: 'paired-comparison', label: 'เทียบทีละคู่', adv: true },
      { id: 'cycle-review', label: 'สรุปรอบประเมิน', adv: true },
    ],
  },
  evaluation: { label: 'ประเมินผล', icon: 'clipboard-check', dynamic: true, tabs: EVALUATION_BASE_TABS },
  engagement: {
    label: 'ความผูกพัน & ชื่นชม',
    icon: 'heart-handshake',
    tabs: [
      { id: 'pulse', label: 'ความรู้สึกประจำสัปดาห์' },
      { id: 'recognition', label: 'ชื่นชมเพื่อนร่วมงาน' },
    ],
  },
  my_dev: {
    label: 'พัฒนาตัวเอง',
    icon: 'rocket',
    tabs: [
      { id: 'my-dev-guide', label: 'คู่มือ' },
      { id: 'dev-plans', label: 'แผนพัฒนา' },
      { id: 'dev-plan-builder', label: 'สร้างแผน' },
      { id: 'learning-calendar', label: 'OKR + ปฏิทิน', adv: true },
      { id: 'mentor-sharing', label: 'พี่เลี้ยง & แชร์ความรู้', adv: true },
      { id: 'certifications', label: 'ใบรับรอง' },
    ],
  },
  l_and_d: {
    label: 'พัฒนา & รักษาคน',
    icon: 'line-chart',
    tabs: [
      { id: 'lnd-guide', label: 'คู่มือ' },
      { id: 'lnd-overview', label: 'ภาพรวม + ROI' },
      { id: 'lnd-plans', label: 'แผน + คน' },
      { id: 'lnd-vendors-courses', label: 'ผู้ให้บริการ + คอร์ส' },
      { id: 'lnd-skill-group', label: 'ทักษะทีม & กลุ่มอบรม', adv: true },
      { id: 'lnd-reports', label: 'รายงาน' },
      { id: 'lnd-settings', label: 'ตั้งค่า L&D', adv: true },
      { id: 'tenant-policies', label: 'ขอบเขตองค์กร (Guard Rail)', adv: true },
    ],
  },
  docs: {
    label: 'เอกสาร',
    icon: 'folder',
    tabs: [
      { id: 'document-generator', label: 'สร้างเอกสาร' },
      { id: 'documents-list', label: 'เอกสารทั้งหมด' },
      { id: 'library', label: 'คลังเอกสาร', adv: true },
      { id: 'documents', label: 'เอกสาร PDF (เดิม)', adv: true },
    ],
  },
  knowledge: { label: 'Knowledge Base', icon: 'book-open', tabs: [{ id: 'assessment-knowledge', label: 'Knowledge Base' }] },
  delegation: {
    label: 'มอบอำนาจ & อนุมัติ',
    icon: 'shield-check',
    // reveal-audit removed (no more exact-salary reveal · addendum 2026-06-02)
    tabs: [
      { id: 'pending-inbox', label: 'คำขอรออนุมัติ' },
    ],
  },
  settings: {
    label: 'ตั้งค่า',
    icon: 'settings',
    tabs: [
      { id: 'settings', label: 'ทั่วไป' },
      { id: 'users', label: 'ผู้ใช้' },
    ],
  },
  admin: { label: 'Admin', icon: 'shield', tabs: [{ id: 'admin', label: 'Admin' }] },
} satisfies Record<string, NavGroup>;

// ── Sidebar IA (flat, role-gated by `g`) — what actually renders ───────────────
// Order = Eval's identity after the SoT move (org → PayPilot): lead with what Eval
// is FOR — ประเมิน → พัฒนา → รักษา → งาน — then พนักงาน/องค์กร as reference, docs,
// settings. "บันทึกผลงาน" folded into "ประเมินผล" (capture = input to evaluation);
// "มอบอำนาจ & อนุมัติ" folded into "ตั้งค่า & สิทธิ์". `g` per item unchanged so
// role-gating + breadcrumb/sub-nav resolution stay intact. (2026-06-02 · 11→9)
export const SIDEBAR_SECTIONS: NavSection[] = [
  {
    id: 'overview',
    title: 'ภาพรวม',
    items: [
      { id: 'dashboard', label: 'แดชบอร์ด', icon: 'layout-dashboard', g: 'dashboard' },
      { id: 'calendar', label: 'ปฏิทินของฉัน', icon: 'calendar', g: 'calendar' },
      { id: 'system-overview', label: 'ความพร้อมของระบบ', icon: 'layout-panel-top', g: 'system_overview' },
      { id: 'blueprint', label: 'Blueprint ระบบ HRD', icon: 'file-text', g: 'system_overview' },
      { id: 'workflow-guide', label: 'ขั้นตอนการทำงาน', icon: 'workflow', g: 'workflow_guide' },
    ],
  },
  {
    id: 'evaluation',
    title: 'ประเมินผล',
    items: [
      { id: 'evaluation', label: 'วิธี & เครื่องมือประเมิน', icon: 'clipboard-check', g: 'evaluation' },
      { id: 'assessment-packages', label: 'ชุดการประเมิน', icon: 'package', g: 'evaluation' },
      // บันทึกผลงาน (capture) = ข้อมูลป้อนการประเมิน — รวมไว้ที่นี่
      { id: 'observations', label: 'บันทึกพฤติกรรมการทำงาน', icon: 'eye', g: 'data_capture' },
      { id: 'incidents', label: 'เหตุการณ์เด่น / ปัญหา', icon: 'flag', g: 'data_capture' },
      { id: 'checkins', label: 'คุยตัวต่อตัว (1:1)', icon: 'message-circle', g: 'data_capture' },
      { id: 'quality-logs', label: 'งานผิด / แก้ซ้ำ', icon: 'alert-triangle', g: 'data_capture', adv: true },
      { id: 'psychometric', label: 'ผลทดสอบบุคลิกภาพ', icon: 'brain', g: 'data_capture', adv: true },
    ],
  },
  {
    id: 'mydev',
    title: 'พัฒนาตัวฉัน',
    items: [
      { id: 'dev-plans', label: 'แผนพัฒนาของฉัน', icon: 'rocket', g: 'my_dev' },
      { id: 'dev-plan-builder', label: 'สร้างแผนพัฒนา', icon: 'plus-circle', g: 'my_dev' },
      { id: 'certifications', label: 'ใบรับรอง / วุฒิบัตร', icon: 'badge-check', g: 'my_dev' },
      { id: 'learning-calendar', label: 'OKR + ปฏิทิน', icon: 'calendar', g: 'my_dev', adv: true },
      { id: 'mentor-sharing', label: 'พี่เลี้ยง & แชร์ความรู้', icon: 'users', g: 'my_dev', adv: true },
    ],
  },
  {
    id: 'lnd',
    title: 'ฝึกอบรม & พัฒนาทีม',
    items: [
      { id: 'lnd-overview', label: 'ภาพรวมการอบรม & ความคุ้มค่า', icon: 'line-chart', g: 'l_and_d' },
      { id: 'lnd-plans', label: 'แผนอบรมรายคน', icon: 'clipboard-list', g: 'l_and_d' },
      { id: 'lnd-vendors-courses', label: 'ผู้สอน & คอร์สอบรม', icon: 'store', g: 'l_and_d' },
      { id: 'lnd-reports', label: 'รายงานการอบรม', icon: 'bar-chart-3', g: 'l_and_d' },
      { id: 'lnd-skill-group', label: 'ทักษะทีม & กลุ่มอบรม', icon: 'users-2', g: 'l_and_d', adv: true },
      { id: 'lnd-settings', label: 'ตั้งค่า L&D', icon: 'settings', g: 'l_and_d', adv: true },
      { id: 'tenant-policies', label: 'ขอบเขตองค์กร', icon: 'shield-check', g: 'l_and_d', adv: true },
    ],
  },
  {
    id: 'engagement',
    title: 'ความผูกพัน & ชื่นชม',
    items: [
      { id: 'pulse', label: 'ความรู้สึกประจำสัปดาห์', icon: 'smile', g: 'engagement' },
      { id: 'recognition', label: 'ชื่นชมเพื่อนร่วมงาน', icon: 'heart', g: 'engagement' },
    ],
  },
  {
    id: 'work',
    title: 'งาน & เป้าหมาย',
    items: [
      { id: 'tasks', label: 'งานที่ต้องทำ', icon: 'kanban-square', g: 'work_hub' },
      { id: 'objectives', label: 'เป้าหมายของทีม', icon: 'target', g: 'work_hub' },
      { id: 'kpi-metrics', label: 'ตัวชี้วัดผลงาน', icon: 'gauge', g: 'work_hub' },
      { id: 'task-templates', label: 'แม่แบบงาน', icon: 'copy', g: 'work_hub', adv: true },
      { id: 'workhub-catalog', label: 'เลือกเครื่องมือ', icon: 'settings-2', g: 'work_hub', adv: true },
      { id: 'ranking', label: 'จัดอันดับพนักงาน', icon: 'list-ordered', g: 'work_hub', adv: true },
      { id: 'paired-comparison', label: 'เทียบทีละคู่', icon: 'shuffle', g: 'work_hub', adv: true },
      { id: 'cycle-review', label: 'สรุปรอบประเมิน', icon: 'check-square', g: 'work_hub', adv: true },
    ],
  },
  {
    id: 'people',
    title: 'พนักงาน & องค์กร',
    items: [
      { id: 'employees', label: 'รายชื่อพนักงาน', icon: 'users', g: 'people' },
      { id: 'onboarding-journey', label: 'เส้นทางต้อนรับ (90 วัน)', icon: 'user-plus', g: 'people' },
      { id: 'probation', label: 'พนักงานทดลองงาน', icon: 'user-check', g: 'people' },
      { id: 'career', label: 'ดาวเด่น & เส้นทางโต', icon: 'grid-3x3', g: 'people' },
      // โครงสร้าง (อ้างอิง — จัดการที่ PayPilot, Eval แสดงอย่างเดียว)
      { id: 'org', label: 'ผังองค์กร', icon: 'network', g: 'org' },
      { id: 'position-jd', label: 'หน้าที่ของแต่ละตำแหน่ง', icon: 'briefcase', g: 'org' },
      { id: 'competency', label: 'ทักษะที่ตำแหน่งต้องมี', icon: 'award', g: 'org' },
      { id: 'core-values', label: 'ค่านิยมองค์กร', icon: 'heart', g: 'org', adv: true },
    ],
  },
  {
    id: 'docs',
    title: 'เอกสาร & คู่มือ',
    items: [
      { id: 'document-generator', label: 'สร้างเอกสาร', icon: 'file-plus', g: 'docs' },
      { id: 'documents-list', label: 'เอกสารทั้งหมด', icon: 'files', g: 'docs' },
      { id: 'assessment-knowledge', label: 'คลังความรู้', icon: 'book-open', g: 'knowledge' },
      { id: 'library', label: 'คลังเอกสาร', icon: 'folder', g: 'docs', adv: true },
      { id: 'documents', label: 'เอกสาร PDF (เดิม)', icon: 'file', g: 'docs', adv: true },
    ],
  },
  {
    id: 'settings',
    title: 'ตั้งค่า & สิทธิ์',
    items: [
      { id: 'settings', label: 'ตั้งค่าทั่วไป', icon: 'settings', g: 'settings' },
      { id: 'users', label: 'ผู้ใช้ & สิทธิ์', icon: 'user-cog', g: 'settings' },
      // มอบอำนาจ & อนุมัติ (owner) — รวมเข้ากลุ่มตั้งค่า
      { id: 'pending-inbox', label: 'คำขอรออนุมัติ', icon: 'inbox', g: 'delegation' },
      { id: 'admin', label: 'ผู้ดูแลระบบ', icon: 'shield', g: 'admin' },
    ],
  },
];

// ── Role → allowed groups. STAFF: NO compensation (Staff-no-salary). ───────────
export const NAV_BY_ROLE: Record<Role, string[]> = {
  SUPER_ADMIN: ['admin', 'dashboard'],
  OWNER: ['dashboard', 'calendar', 'system_overview', 'workflow_guide', 'org', 'people', 'my_dev', 'l_and_d', 'data_capture', 'work_hub', 'evaluation', 'engagement', 'docs', 'knowledge', 'delegation', 'settings'],
  HR: ['dashboard', 'calendar', 'system_overview', 'workflow_guide', 'org', 'people', 'my_dev', 'l_and_d', 'data_capture', 'work_hub', 'evaluation', 'engagement', 'docs', 'knowledge', 'settings'],
  MANAGER: ['dashboard', 'calendar', 'system_overview', 'workflow_guide', 'people', 'my_dev', 'data_capture', 'work_hub', 'evaluation', 'engagement', 'docs', 'knowledge', 'settings'],
  LEAD: ['dashboard', 'calendar', 'my_dev', 'data_capture', 'work_hub', 'engagement', 'docs', 'knowledge', 'settings'],
  STAFF: ['calendar', 'my_dev', 'work_hub', 'engagement', 'docs', 'knowledge', 'settings'],
};

export function allowedGroups(role: Role | undefined): Set<string> {
  return new Set(NAV_BY_ROLE[role ?? 'STAFF'] ?? NAV_BY_ROLE.STAFF);
}

export function isGroupAllowed(role: Role | undefined, group: string): boolean {
  return allowedGroups(role).has(group);
}

/** Is a route reachable by this role? (route id → its group via SIDEBAR_SECTIONS). */
export function isRouteAllowed(role: Role | undefined, routeId: string): boolean {
  const group = groupOfRoute(routeId);
  if (!group) return true; // unmapped (full-screen / utility) routes aren't gated here
  return isGroupAllowed(role, group);
}

const ROUTE_TO_GROUP: Record<string, string> = (() => {
  const map: Record<string, string> = {};
  for (const section of SIDEBAR_SECTIONS) {
    for (const item of section.items) map[item.id] = item.g;
  }
  return map;
})();

export function groupOfRoute(routeId: string): string | undefined {
  if (routeId.startsWith('assess-')) return 'evaluation';
  return ROUTE_TO_GROUP[routeId];
}

// ── Dynamic tab resolution (parameterized — no module global) ──────────────────
export function computeEvaluationTabs(ctx: EvalContext = EMPTY_EVAL_CONTEXT): NavTab[] {
  const tabs = EVALUATION_BASE_TABS.slice();
  const seen = new Set(tabs.map((t) => t.id));
  for (const code of ctx.enabledMethods) {
    const method = ctx.methodCatalog.find((m) => m.code === code);
    if (!method) continue;
    const id = method.existing_page || `assess-${code}`;
    if (seen.has(id)) continue;
    seen.add(id);
    tabs.push({ id, label: method.name_th || method.name || code, methodCode: code });
  }
  return tabs;
}

export function computeWorkhubTabs(
  groupKey: string,
  staticHead: NavTab[],
  allTabs: NavTab[],
  ctx: EvalContext = EMPTY_EVAL_CONTEXT,
): NavTab[] {
  const effective = new Set(ctx.workhubEffective);
  const modules = ctx.workhubModules.filter((m) => m.group === groupKey);
  // Graceful fallback: modules not loaded yet → show everything.
  if (!ctx.workhubModules.length) return [...staticHead, ...allTabs];
  const visibleTabIds = new Set<string>();
  for (const m of modules) {
    if (effective.has(m.id)) visibleTabIds.add(m.tab_id);
  }
  return [...staticHead, ...allTabs.filter((t) => visibleTabIds.has(t.id))];
}

export function effectiveTabs(group: NavGroup, ctx: EvalContext = EMPTY_EVAL_CONTEXT): NavTab[] {
  if (group === NAV_GROUPS.evaluation) return computeEvaluationTabs(ctx);
  if (group === NAV_GROUPS.work_hub || group === NAV_GROUPS.data_capture) {
    const key = group === NAV_GROUPS.work_hub ? 'work_hub' : 'data_capture';
    return computeWorkhubTabs(key, group.staticHead ?? [], group.tabs, ctx);
  }
  return group.tabs;
}

export interface ResolvedGroup {
  groupId: string;
  group: NavGroup;
}

/** Find the group containing a route (drives SubNav + breadcrumb). Mirrors the
 *  vanilla priority: assess-* and dynamic tabs resolve to `evaluation` first. */
export function findNavGroup(routeId: string, ctx: EvalContext = EMPTY_EVAL_CONTEXT): ResolvedGroup | null {
  if (routeId.startsWith('assess-')) {
    return { groupId: 'evaluation', group: { ...NAV_GROUPS.evaluation, tabs: computeEvaluationTabs(ctx) } };
  }
  if (routeId === 'workhub-catalog') {
    return { groupId: 'work_hub', group: { ...NAV_GROUPS.work_hub, tabs: effectiveTabs(NAV_GROUPS.work_hub, ctx) } };
  }
  const evalTabs = computeEvaluationTabs(ctx);
  if (evalTabs.some((t) => t.id === routeId)) {
    return { groupId: 'evaluation', group: { ...NAV_GROUPS.evaluation, tabs: evalTabs } };
  }
  for (const groupId of ['work_hub', 'data_capture'] as const) {
    const g = NAV_GROUPS[groupId];
    const fullTabs = [...(g.staticHead ?? []), ...g.tabs];
    if (fullTabs.some((t) => t.id === routeId)) {
      return { groupId, group: { ...g, tabs: effectiveTabs(g, ctx) } };
    }
  }
  for (const [groupId, group] of Object.entries(NAV_GROUPS)) {
    if (effectiveTabs(group, ctx).some((t) => t.id === routeId)) {
      return { groupId, group: { ...group, tabs: effectiveTabs(group, ctx) } };
    }
  }
  return null;
}

// ── Sidebar selector (role-gated + UI-mode filtered, empty sections dropped) ───
export function sectionsForRole(role: Role | undefined, mode: UiMode = 'full'): NavSection[] {
  const allowed = allowedGroups(role);
  const out: NavSection[] = [];
  for (const section of SIDEBAR_SECTIONS) {
    const items = section.items.filter((it) => allowed.has(it.g) && (mode === 'full' || !it.adv));
    if (items.length) out.push({ ...section, items });
  }
  return out;
}
